import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CircleNotch } from 'phosphor-react';
import { ENABLE_GOOGLE_AUTH } from '../lib/featureFlags';
import {
  buildAuthPageUrl,
  getRedirectDestination,
  normalizeProductValue,
  resolvePostAuthDestination,
  setPendingProductIntent,
} from '../lib/productRouting';

// Vertical logo (brain + GENI)
const LOGO_VERTICAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/zc7oaxip_brain%20%2B%20GENICERTO.png";

// Google Icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isSubmittingRef = useRef(false);
  const product = normalizeProductValue(searchParams.get('product'));
  
  // Calculate redirect destination once
  const redirectDestination = getRedirectDestination(searchParams, location.state);

  useEffect(() => {
    if (product) {
      setPendingProductIntent(product);
    }
  }, [product]);

  // Map auth error to user-friendly message
  const getAuthErrorMessage = (error) => {
    const message = (error.message || '').toLowerCase();
    const code = error.code || '';
    
    // Invalid credentials (wrong email or password)
    if (message.includes('invalid login') || 
        message.includes('invalid credentials') ||
        message.includes('invalid email') ||
        message.includes('invalid password') ||
        message.includes('wrong password') ||
        message.includes('user not found') ||
        message.includes('no user') ||
        code.includes('invalid_credentials') ||
        code.includes('user_not_found')) {
      return 'Incorrect email or password';
    }
    
    // Rate limiting
    if (message.includes('rate') || 
        message.includes('too many') ||
        message.includes('429') ||
        message.includes('limit') ||
        code.includes('rate_limit') ||
        code.includes('too_many_requests')) {
      return 'Too many attempts. Please wait a moment and try again';
    }
    
    // Email not confirmed
    if (message.includes('email not confirmed') || 
        message.includes('confirm your email') ||
        message.includes('verify your email') ||
        message.includes('email confirmation') ||
        code.includes('email_not_confirmed')) {
      return 'Please confirm your email before signing in';
    }
    
    // Network/connection errors
    if (message.includes('network') || 
        message.includes('connection') ||
        message.includes('fetch') ||
        message.includes('timeout') ||
        message.includes('failed to fetch') ||
        message.includes('offline') ||
        message.includes('522') ||
        message.includes('timed out') ||
        code.includes('network_error')) {
      return 'The authentication service is temporarily unavailable (Error 522/Timeout). Your credentials were not verified. Please try again in a few moments.';
    }
    
    // Server errors
    if (message.includes('500') || 
        message.includes('502') ||
        message.includes('503') ||
        message.includes('server error') ||
        message.includes('internal error')) {
      return 'Connection error. Please try again';
    }
    
    // Default fallback
    return 'Unexpected sign-in error';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions - check both ref and state
    if (isSubmittingRef.current || loading || cooldown) {
      console.log('[Login] Blocked duplicate submission');
      return;
    }
    
    isSubmittingRef.current = true;
    setLoading(true);
    setError('');

    try {
      await signIn(email, password);
      const destination = await resolvePostAuthDestination({
        requestedRedirect: redirectDestination,
        allowAcquisition: false,
      });
      navigate(destination, { replace: true });
    } catch (err) {
      const friendlyMessage = getAuthErrorMessage(err);
      setError(friendlyMessage);
      
      // Add cooldown for rate limit errors
      if (friendlyMessage.includes('Too many attempts')) {
        setCooldown(true);
        setTimeout(() => setCooldown(false), 3000);
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleGoogleSignIn = async () => {
    if (googleLoading) return;
    
    setGoogleLoading(true);
    setError('');
    
    try {
      // Pass the intended redirect destination to OAuth flow
      await signInWithGoogle(redirectDestination, { allowAcquisition: false });
      // OAuth will redirect, so we don't need to navigate here
    } catch (err) {
      setError('Failed to connect with Google. Please try again.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Vertical Version */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img
            src={LOGO_VERTICAL}
            alt="GENI"
            className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
          />
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-sm p-8">
          <h2 className="text-3xl font-bold font-heading text-white mb-2">Welcome back.</h2>
          <p className="text-white/60 mb-6">Stay disciplined. Your edge comes from consistency. Let's keep building it.</p>

          {/* Google Sign In Button - Controlled by feature flag */}
          {ENABLE_GOOGLE_AUTH && (
            <>
              <Button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="w-full bg-white text-gray-700 font-medium hover:bg-gray-100 transition-colors rounded-sm mb-4 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="google-signin"
              >
                {googleLoading ? (
                  <CircleNotch size={18} className="animate-spin text-gray-600" />
                ) : (
                  <GoogleIcon />
                )}
                {googleLoading ? 'Connecting...' : 'Continue with Google'}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-[#121212] px-3 text-white/40">or</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-white/80 mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white/80 mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="login-password"
              />
              <div className="mt-2 text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-white/50 hover:text-white/80 text-sm transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3" data-testid="login-error">
                <p className="text-[#FF3B30] text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || cooldown}
              className="w-full bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <CircleNotch size={18} className="animate-spin" />
                  Signing in...
                </span>
              ) : cooldown ? (
                'Please wait...'
              ) : (
                'Sign In'
              )}
            </Button>

            <p className="text-center text-white/60 text-sm">
              Don't have an account?{' '}
              <Link 
                to={buildAuthPageUrl('/signup', redirectDestination, product)}
                className="text-[#00E676] hover:underline"
              >
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
